import React, { useState, useEffect } from "react";
import { CheckCircle2, Loader2, Download, ShieldCheck, ExternalLink } from "lucide-react";
import { api, API } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { toast } from "sonner";

const PRESETS = [25, 50, 100, 250, 500, 1000];

export default function SupportCheckout({ open, onOpenChange, campaign }) {
  const { settings } = useSettings();
  const providers = settings?.payment_providers || {};
  const showStripe = providers.stripe !== false;
  const showPaypal = providers.paypal !== false;
  const bothShown = showStripe && showPaypal;
  const currencies = settings?.currencies || ["AED", "USD"];
  const [amount, setAmount] = useState(100);
  const [currency, setCurrency] = useState(campaign?.currency || "AED");
  const [form, setForm] = useState({ supporter_name: "", email: "", phone: "", country: "", company: "", anonymous: false, communication_consent: false, terms_accepted: false });
  const [step, setStep] = useState("form"); // form | processing | done
  const [result, setResult] = useState(null);
  const [method, setMethod] = useState("card"); // card (Stripe) | paypal

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (method === "card" && !showStripe) setMethod("paypal");
    else if (method === "paypal" && !showPaypal) setMethod("card");
  }, [showStripe, showPaypal]); // eslint-disable-line

  const submit = async () => {
    if (!form.supporter_name || !form.email) return toast.error("Please enter your name and email.");
    if (!form.terms_accepted) return toast.error("Please accept the terms to continue.");
    if (!amount || amount <= 0) return toast.error("Please enter a valid amount.");
    setStep("processing");
    const classification = campaign.category === "islamic" ? "Sadaqah" : "General CSR";
    try {
      if (method === "card") {
        const { data } = await api.post("/payments/stripe/create-session", {
          campaign_slug: campaign.slug, amount: Number(amount), currency, ...form,
          classification, origin_url: window.location.origin,
        });
        localStorage.setItem("hh_stripe_session", data.session_id);
        localStorage.setItem("hh_stripe_campaign", campaign.title);
        window.location.href = data.url;
        return;
      }
      const { data } = await api.post("/payments/create-order", {
        campaign_slug: campaign.slug, amount: Number(amount), currency,
        provider: "paypal", ...form, classification,
      });
      if (data.approve_url) {
        window.location.href = data.approve_url;
        return;
      }
      const cap = await api.post("/payments/capture", { order_id: data.order_id });
      setResult(cap.data);
      setStep("done");
    } catch (e) {
      setStep("form");
      toast.error(e?.response?.data?.detail || "Your payment could not be completed. No successful payment has been recorded.");
    }
  };

  const close = () => { onOpenChange(false); setTimeout(() => { setStep("form"); setResult(null); }, 300); };

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? close() : onOpenChange(v))}>
      <DialogContent data-testid="support-checkout-dialog" className="max-h-[92vh] overflow-y-auto bg-white sm:max-w-lg">
        {step === "done" ? (
          <div className="py-4 text-center" data-testid="checkout-success">
            <DialogTitle className="sr-only">Contribution confirmed</DialogTitle>
            <DialogDescription className="sr-only">Your contribution has been recorded and a receipt generated.</DialogDescription>
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-600" />
            <h3 className="mt-4 font-display text-2xl font-bold text-emerald-900">Thank you for your support</h3>
            <p className="mt-1 text-sm text-slate-600">Your contribution to <b>{campaign.title}</b> has been recorded.</p>
            {result?.receipt_number && (
              <div className="mt-6 rounded-2xl border border-emerald-900/10 bg-sand p-5 text-start">
                <div className="flex justify-between text-sm"><span className="text-slate-500">Receipt Number</span><b className="text-emerald-900">{result.receipt_number}</b></div>
                <div className="mt-1.5 flex justify-between text-sm"><span className="text-slate-500">Reference</span><span className="text-emerald-900">{result.transaction_ref}</span></div>
                <div className="mt-4 flex flex-col gap-2">
                  <a href={`${API}/receipts/${result.receipt_number}/pdf`} target="_blank" rel="noreferrer" data-testid="download-receipt" className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-emerald-800"><Download className="h-4 w-4" /> Download PDF Receipt</a>
                  <a href={`/verify-receipt/${result.receipt_number}`} className="inline-flex items-center justify-center gap-2 rounded-full border border-emerald-900/20 px-5 py-2.5 text-sm font-semibold text-emerald-900 hover:bg-emerald-50"><ExternalLink className="h-4 w-4" /> Verify Receipt</a>
                </div>
              </div>
            )}
            <button onClick={close} className="mt-5 text-sm font-semibold text-slate-500 hover:text-emerald-900">Close</button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl text-emerald-900">Support {campaign?.title}</DialogTitle>
              <DialogDescription className="text-slate-500">Choose an amount and enter your details. Payments are verified server-side.</DialogDescription>
            </DialogHeader>
            {(method === "paypal" && settings && !settings.paypal_configured) && (
              <div className="rounded-lg bg-gold-soft px-3 py-2 text-xs font-semibold text-emerald-900">TEST MODE — PayPal keys not yet configured. Transactions are simulated for demonstration.</div>
            )}
            {method === "card" && (
              settings?.stripe_test_mode ? (
                <div className="rounded-lg bg-gold-soft px-3 py-2 text-xs font-semibold text-emerald-900">Secure card payment via Stripe. Test mode — use card 4242 4242 4242 4242, any future expiry & CVC.</div>
              ) : (
                <div className="rounded-lg bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-900">Secure card payment via Stripe. Your payment is encrypted and verified server-side.</div>
              )
            )}
            <div className="space-y-4">
              <div>
                <Label>Payment method</Label>
                <div className={`mt-2 grid gap-2 ${bothShown ? "grid-cols-2" : "grid-cols-1"}`}>
                  {showStripe && (
                    <button type="button" onClick={() => setMethod("card")} data-testid="method-card" className={`rounded-lg border py-2.5 text-sm font-bold transition ${method === "card" ? "border-emerald-900 bg-emerald-900 text-white" : "border-emerald-900/15 text-emerald-900 hover:bg-emerald-50"}`}>Card (Stripe)</button>
                  )}
                  {showPaypal && (
                    <button type="button" onClick={() => setMethod("paypal")} data-testid="method-paypal" className={`rounded-lg border py-2.5 text-sm font-bold transition ${method === "paypal" ? "border-emerald-900 bg-emerald-900 text-white" : "border-emerald-900/15 text-emerald-900 hover:bg-emerald-50"}`}>PayPal</button>
                  )}
                </div>
              </div>
              <div>
                <Label>Amount</Label>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {PRESETS.map((p) => (
                    <button key={p} type="button" onClick={() => setAmount(p)} data-testid={`amount-${p}`} className={`rounded-lg border py-2 text-sm font-bold transition ${Number(amount) === p ? "border-emerald-900 bg-emerald-900 text-white" : "border-emerald-900/15 text-emerald-900 hover:bg-emerald-50"}`}>{p}</button>
                  ))}
                </div>
                <div className="mt-2 flex gap-2">
                  <Input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="custom-amount" className="flex-1" placeholder="Custom amount" />
                  <Select value={currency} onValueChange={setCurrency}>
                    <SelectTrigger className="w-28" data-testid="currency-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{currencies.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Full name *</Label><Input value={form.supporter_name} onChange={(e) => set("supporter_name", e.target.value)} data-testid="supporter-name" className="mt-1.5" /></div>
                <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="supporter-email" className="mt-1.5" /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5" /></div>
                <div><Label>Country</Label><Input value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="supporter-country" className="mt-1.5" /></div>
                <div><Label>Company</Label><Input value={form.company} onChange={(e) => set("company", e.target.value)} className="mt-1.5" /></div>
              </div>

              <div className="space-y-2.5 pt-1">
                <label className="flex items-start gap-2.5 text-sm text-slate-600"><Checkbox checked={form.anonymous} onCheckedChange={(v) => set("anonymous", !!v)} data-testid="anonymous-check" className="mt-0.5" /> Make my participation anonymous on any public listing.</label>
                <label className="flex items-start gap-2.5 text-sm text-slate-600"><Checkbox checked={form.communication_consent} onCheckedChange={(v) => set("communication_consent", !!v)} className="mt-0.5" /> I consent to receive updates (optional). Marketing requires separate consent.</label>
                <label className="flex items-start gap-2.5 text-sm text-slate-600"><Checkbox checked={form.terms_accepted} onCheckedChange={(v) => set("terms_accepted", !!v)} data-testid="terms-check" className="mt-0.5" /> I accept the Payment Terms and Responsible Giving Policy. *</label>
              </div>

              <p className="flex items-center gap-1.5 text-xs text-slate-500"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Payments are verified server-side. We never store card details.</p>

              <button onClick={submit} disabled={step === "processing"} data-testid="checkout-submit" className="flex w-full items-center justify-center gap-2 rounded-full bg-emerald-900 px-6 py-3.5 text-sm font-bold text-white transition hover:bg-emerald-800 disabled:opacity-60">
                {step === "processing" ? <><Loader2 className="h-4 w-4 animate-spin" /> Processing…</> : <>Contribute {currency} {Number(amount || 0).toLocaleString()}</>}
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
